import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2 } from "lucide-react";
import client, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AdminLayout from "./AdminLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const ROLES = [
  ["super_admin", "Super Admin"],
  ["content_admin", "Content Admin"],
  ["enquiry_admin", "Enquiry Admin"],
  ["viewer", "Viewer"],
];
const roleLabel = (r) => (ROLES.find((x) => x[0] === r)?.[1] || r);
const NEW = { name: "", email: "", password: "", role: "content_admin" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(NEW);
  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");

  const load = () => client.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await client.post("/admin/users", form); toast.success("User created."); setForm(NEW); setAdding(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patch = async (id, body) => {
    try { await client.put(`/admin/users/${id}`, body); toast.success("Updated."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reset = async () => {
    try { await client.post(`/admin/users/${resetFor.id}/reset-password`, { newPassword: newPass }); toast.success("Password reset."); setResetFor(null); setNewPass(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This cannot be undone.`)) return;
    try { await client.delete(`/admin/users/${u.id}`); toast.success("User removed."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="User Management">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-ink-muted">Manage administrator accounts and roles. Passwords are never displayed.</p>
        <button onClick={() => setAdding(true)} data-testid="user-add" className="inline-flex items-center gap-2 bg-maroon px-5 py-2.5 text-xs uppercase tracking-wide text-ivory hover:bg-maroon-dark"><UserPlus size={16} /> Add User</button>
      </div>

      <div className="overflow-x-auto rounded-md border border-gold/20 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gold/20 text-left text-xs uppercase tracking-wide text-ink-muted">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Last Login</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-gold/10" data-testid={`user-row-${u.id}`}>
                <td className="p-3 text-maroon">{u.name}{u.id === user?.id && <span className="ml-2 text-[10px] text-gold-dark">(you)</span>}</td>
                <td className="p-3 text-ink-muted">{u.email}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })}>
                    <SelectTrigger className="h-8 w-40" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <button onClick={() => patch(u.id, { status: u.status === "active" ? "inactive" : "active" })} data-testid={`user-status-${u.id}`}
                    className={`rounded-sm px-2 py-1 text-xs ${u.status === "active" ? "bg-maroon text-ivory" : "bg-ivory text-ink-muted"}`}>
                    {u.status === "active" ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="p-3 text-ink-muted">{(u.lastLogin || "").slice(0, 10) || "—"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setResetFor(u)} className="mr-3 inline-flex items-center gap-1 text-xs text-maroon hover:underline" data-testid={`user-reset-${u.id}`}><KeyRound size={13} /> Reset</button>
                  <button onClick={() => remove(u)} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" data-testid={`user-delete-${u.id}`}><Trash2 size={13} /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAdding(false)}>
          <div className="w-full max-w-md rounded-md bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="user-add-modal">
            <h2 className="mb-4 font-serif text-2xl text-maroon">Add Administrator</h2>
            <div className="space-y-4">
              <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label>Email</Label><Input className="mt-1.5" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label>Temporary Password</Label><Input className="mt-1.5" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /><p className="mt-1 text-xs text-ink-muted">Minimum 8 characters.</p></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={create} data-testid="user-create-submit" className="flex-1 bg-maroon px-5 py-3 text-xs uppercase tracking-wide text-ivory">Create</button>
              <button onClick={() => setAdding(false)} className="flex-1 border border-gold/40 px-5 py-3 text-xs uppercase tracking-wide text-maroon">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {resetFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setResetFor(null)}>
          <div className="w-full max-w-sm rounded-md bg-white p-7" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 text-lg font-medium text-maroon">Reset password — {resetFor.email}</h3>
            <Input type="password" placeholder="New password (min 8)" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="user-reset-input" />
            <div className="mt-5 flex gap-3">
              <button onClick={reset} data-testid="user-reset-submit" className="flex-1 bg-maroon px-4 py-2.5 text-xs uppercase text-ivory">Set Password</button>
              <button onClick={() => setResetFor(null)} className="flex-1 border border-gold/40 px-4 py-2.5 text-xs uppercase text-maroon">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
